import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { languages } from '../../../../../../../../configs/language'
import { Element } from '../../../../../../../common/html/element'
import { Loading } from '../../../../../../../common/loading'
import { Notification } from '../../../../../../../common/notification'
import { Panel } from '../../../../../../../common/panel'
import { valToPercent } from '../../../../../../../helpers/value'
import { selectTypePeriod } from '../../../../../store/slice'
import { selectCommentary, selectLoading } from '../../../../store/slice'
import { getCommentaryThunk } from '../../../../store/thunk'

const Commentary = ({ isDynamicReport }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const locale = useSelector((state) => state.i18n.locale)
  const timeFrequency = useSelector(selectTypePeriod)
  const isLoading = useSelector(selectLoading('commentary'))
  const commentary = useSelector(selectCommentary)
  const params = new Proxy(new URLSearchParams(location.search), {
    get: (searchParam, prop) => searchParam.get(prop),
  })

  useEffect(() => {
    dispatch(
      getCommentaryThunk({
        OrganizationId: params.organizationId,
        TimeFrequency: timeFrequency,
      }),
    )
  }, [locale, timeFrequency])

  const {
    colorCompare,
    scoreCompany,
    pd,
    scoreSegment,
    riskCategory,
    generate_risk_interpretation_1,
    generate_risk_interpretation_2,
    textCompareScore,
    factors,
    isAdjusted,
    riskGradeTypeCode,
    overDescriptions,
    scoreValueInitial,
    scoreValue,
    diffScore,
    colorRiskCategory,
  } = commentary || {}

  const renderContentIsAdjusted = () => {
    if (locale === languages.en) {
      return (
        <>
          <Element style={{ lineHeight: '20px' }}>
            The initial corporate risk score is&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {scoreValueInitial},
            </Element>
            &nbsp;and the adjusted score is&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {scoreValue}
            </Element>
            ,&nbsp;which corresponds to the risk grade of&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {riskGradeTypeCode}
            </Element>
            .&nbsp;The adjustment is due to elevated risk concerns identified
            during the review, including:
          </Element>
          <Element className="mt1">
            {Array.isArray(overDescriptions) &&
              overDescriptions.map((item) => (
                <>
                  ●&nbsp;{item.descirption}
                  <br />
                </>
              ))}
          </Element>
        </>
      )
    } else {
      return (
        <>
          <Element style={{ lineHeight: '20px' }}>
            Điểm rủi ro doanh nghiệp ban đầu là&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {scoreValueInitial},
            </Element>
            &nbsp;điểm sau khi điều chỉnh là&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {scoreValue}
            </Element>
            ,&nbsp;tương ứng với hạng rủi ro&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {riskGradeTypeCode}
            </Element>
            .&nbsp;Việc điều chỉnh được thực hiện do ghi nhận các yếu tố rủi ro
            gia tăng trong quá trình rà soát, bao gồm:
          </Element>
          <Element className="mt1">
            {Array.isArray(overDescriptions) &&
              overDescriptions.map((item) => (
                <>
                  ●&nbsp;{item.descirption}
                  <br />
                </>
              ))}
          </Element>
        </>
      )
    }
  }

  const renderContentFG18 = () => {
    if (locale === languages.en) {
      return (
        <Element className="dFlex">
          The company’s current score is&nbsp;
          <Element style={{ fontWeight: 600 }}>{scoreCompany},</Element>
          &nbsp;corresponding to a PD of&nbsp;
          <Element style={{ fontWeight: 600 }}>
            {valToPercent(pd, locale, false, false, 0)}
          </Element>
          &nbsp;(
          <Element style={{ fontWeight: 600, color: colorCompare }}>
            {riskCategory}
          </Element>
          ).
        </Element>
      )
    }
    return (
      <Element className="dFlex">
        Điểm số hiện tại của công ty là&nbsp;
        <Element style={{ fontWeight: 600 }}>{scoreCompany},</Element>
        &nbsp;tương ứng với mức xác suất vỡ nợ là&nbsp;
        <Element style={{ fontWeight: 600 }}>
          {valToPercent(pd, locale, false, false, 0)}
        </Element>
        &nbsp;(
        <Element style={{ fontWeight: 600, color: colorCompare }}>
          {riskCategory}
        </Element>
        ).
      </Element>
    )
  }

  const renderContentFGOther = () => {
    if (locale === languages.en) {
      return (
        <Element elementName="span" style={{ lineHeight: '20px' }}>
          <Element elementName="span" style={{ whiteSpace: 'normal' }}>
            The company’s current score is&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {scoreCompany}
            </Element>
            , corresponding to a PD of&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {valToPercent(pd, locale, false)}
            </Element>
            &nbsp;(
            <Element
              elementName="span"
              style={{ fontWeight: 600, color: colorRiskCategory }}
            >
              {riskCategory}
            </Element>
            ). Compared to the model segment benchmark score of&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {scoreSegment}
            </Element>
            ,&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {generate_risk_interpretation_1}
            </Element>
            .
          </Element>
          <br />
          <Element elementName="span" style={{ whiteSpace: 'normal' }}>
            Compared to the previous year, the current score has&nbsp;
            <Element
              elementName="span"
              style={{
                fontWeight: 600,
                color: colorCompare,
              }}
            >
              {textCompareScore}&nbsp;
              {diffScore ? <>by {Math.abs(diffScore)} points</> : null}
            </Element>
            , which may&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {generate_risk_interpretation_2}
            </Element>
            . The following risk drivers have the most significant impact on the
            company’s overall score.
          </Element>
          <Element className="mt1">
            <Element>
              ●&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {factors?.[0]?.factorName}
              </Element>
              &nbsp;is the first most impactful factor influencing the company’s
              overall score. Its current score is&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {factors?.[0]?.scoreFactor}
              </Element>
              ,&nbsp;
              <Element
                elementName="span"
                style={{ fontWeight: 600, color: factors?.[0]?.colorCompare }}
              >
                {factors?.[0]?.textCompareScore}
              </Element>
              &nbsp;than the model segment median of&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {factors?.[0]?.scoreMedianSegment}
              </Element>
              , corresponding to a relative contribution of&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {valToPercent(
                  factors?.[0]?.relativeFactor,
                  locale,
                  true,
                  false,
                  4,
                )}
              </Element>
              &nbsp;to the model segment benchmark. This reflects that&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {factors?.[0]?.generate_risk_interpretation_X}
              </Element>
            </Element>
            <Element>
              ●&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {factors?.[1]?.factorName}
              </Element>
              &nbsp;is the second most impactful factor influencing the
              company’s overall score. Its current score is&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {factors?.[1]?.scoreFactor}
              </Element>
              ,&nbsp;
              <Element
                elementName="span"
                style={{ fontWeight: 600, color: factors?.[1]?.colorCompare }}
              >
                {factors?.[1]?.textCompareScore}
              </Element>
              &nbsp;than the model segment median of&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {factors?.[1]?.scoreMedianSegment}
              </Element>
              , corresponding to a relative contribution of&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {valToPercent(
                  factors?.[1]?.relativeFactor,
                  locale,
                  true,
                  false,
                  4,
                )}
              </Element>
              &nbsp;to the model segment benchmark. This reflects that&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {factors?.[1]?.generate_risk_interpretation_X}
              </Element>
            </Element>
            <Element>
              ●&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {factors?.[2]?.factorName}
              </Element>
              &nbsp;is the third most impactful factor influencing the company’s
              overall score. Its current score is&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {factors?.[2]?.scoreFactor}
              </Element>
              ,&nbsp;
              <Element
                elementName="span"
                style={{ fontWeight: 600, color: factors?.[2]?.colorCompare }}
              >
                {factors?.[2]?.textCompareScore}
              </Element>
              &nbsp;than the model segment median of&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {factors?.[2]?.scoreMedianSegment}
              </Element>
              , corresponding to a relative contribution of&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {valToPercent(
                  factors?.[2]?.relativeFactor,
                  locale,
                  true,
                  false,
                  4,
                )}
              </Element>
              &nbsp;to the model segment benchmark. This reflects that&nbsp;
              <Element elementName="span" style={{ fontWeight: 600 }}>
                {factors?.[2]?.generate_risk_interpretation_X}
              </Element>
            </Element>
          </Element>
        </Element>
      )
    }
    return (
      <Element elementName="span" style={{ lineHeight: '20px' }}>
        <Element elementName="span" style={{ whiteSpace: 'normal' }}>
          Điểm số hiện tại của công ty là&nbsp;
          <Element elementName="span" style={{ fontWeight: 600 }}>
            {scoreCompany}
          </Element>
          , tương ứng với mức xác suất vỡ nợ là&nbsp;
          <Element elementName="span" style={{ fontWeight: 600 }}>
            {valToPercent(pd, locale, false)}
          </Element>
          &nbsp;(
          <Element
            elementName="span"
            style={{ fontWeight: 600, color: colorRiskCategory }}
          >
            {riskCategory}
          </Element>
          ). So với điểm chuẩn trung vị của phân khúc mô hình là&nbsp;
          <Element elementName="span" style={{ fontWeight: 600 }}>
            {scoreSegment}
          </Element>
          ,&nbsp;
          <Element elementName="span" style={{ fontWeight: 600 }}>
            {generate_risk_interpretation_1}
          </Element>
          .
        </Element>
        <br />
        <Element elementName="span">
          So với năm trước, điểm số hiện tại đã&nbsp;
          <Element
            elementName="span"
            style={{
              fontWeight: 600,
              color: colorCompare,
            }}
          >
            {textCompareScore}&nbsp;
            {diffScore ? <>{Math.abs(diffScore)} điểm</> : null}
          </Element>
          , điều này có thể&nbsp;
          <Element elementName="span" style={{ fontWeight: 600 }}>
            {generate_risk_interpretation_2}
          </Element>
          . Dưới đây là những yếu tố có tác động đáng kể nhất đến điểm số tổng
          thể của công ty.
        </Element>
        <Element className="mt1">
          <Element>
            ●&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {factors?.[0]?.factorName}
            </Element>
            &nbsp;là nhân tố ảnh hưởng lớn nhất đến điểm số tổng thể của công
            ty. Điểm số hiện tại là&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {factors?.[0]?.scoreFactor}
            </Element>
            ,&nbsp;
            <Element
              elementName="span"
              style={{ fontWeight: 600, color: factors?.[0]?.colorCompare }}
            >
              {factors?.[0]?.textCompareScore}
            </Element>
            &nbsp;so với trung vị của nhân tố trong phân khúc mô hình là&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {factors?.[0]?.scoreMedianSegment}
            </Element>
            , tương ứng với mức đóng góp tương đối là&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {valToPercent(
                factors?.[0]?.relativeFactor,
                locale,
                true,
                false,
                4,
              )}
            </Element>
            &nbsp;vào điểm chuẩn trung vị của phân khúc mô hình. Điều này phản
            ánh rằng&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {factors?.[0]?.generate_risk_interpretation_X}
            </Element>
          </Element>
          <Element>
            ●&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {factors?.[1]?.factorName}
            </Element>
            &nbsp;là nhân tố ảnh hưởng lớn thứ hai đến điểm số tổng thể của công
            ty. Điểm số hiện tại là&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {factors?.[1]?.scoreFactor}
            </Element>
            ,&nbsp;
            <Element
              elementName="span"
              style={{ fontWeight: 600, color: factors?.[1]?.colorCompare }}
            >
              {factors?.[1]?.textCompareScore}
            </Element>
            &nbsp;so với trung vị của nhân tố trong phân khúc mô hình là&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {factors?.[1]?.scoreMedianSegment}
            </Element>
            , tương ứng với mức đóng góp tương đối là&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {valToPercent(
                factors?.[1]?.relativeFactor,
                locale,
                true,
                false,
                4,
              )}
            </Element>
            &nbsp;vào điểm chuẩn trung vị của phân khúc mô hình. Điều này phản
            ánh rằng&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {factors?.[1]?.generate_risk_interpretation_X}
            </Element>
          </Element>
          <Element>
            ●&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {factors?.[2]?.factorName}
            </Element>
            &nbsp;là nhân tố ảnh hưởng lớn thứ ba đến điểm số tổng thể của công
            ty. Điểm số hiện tại là&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {factors?.[2]?.scoreFactor}
            </Element>
            ,&nbsp;
            <Element
              elementName="span"
              style={{ fontWeight: 600, color: factors?.[2]?.colorCompare }}
            >
              {factors?.[2]?.textCompareScore}
            </Element>
            &nbsp;so với trung vị của nhân tố trong phân khúc mô hình là&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {factors?.[2]?.scoreMedianSegment}
            </Element>
            , tương ứng với mức đóng góp tương đối là&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {valToPercent(
                factors?.[2]?.relativeFactor,
                locale,
                true,
                false,
                4,
              )}
            </Element>
            &nbsp;vào điểm chuẩn trung vị của phân khúc mô hình. Điều này phản
            ánh rằng&nbsp;
            <Element elementName="span" style={{ fontWeight: 600 }}>
              {factors?.[2]?.generate_risk_interpretation_X}
            </Element>
          </Element>
        </Element>
      </Element>
    )
  }

  if (isDynamicReport) {
    return (
      <div style={{ padding: 12, position: 'relative' }}>
        {!commentary || Object.keys(commentary).length === 0 ? (
          <div style={{ height: 200 }}>
            <Notification />
          </div>
        ) : isAdjusted ? (
          renderContentIsAdjusted()
        ) : !scoreCompany ? (
          renderContentFG18()
        ) : (
          renderContentFGOther()
        )}
      </div>
    )
  }

  return (
    <Panel isI18n label="commentary.title">
      <div style={{ padding: 12, position: 'relative' }}>
        {isLoading ? (
          <Loading />
        ) : !commentary || Object.keys(commentary).length === 0 ? (
          <div style={{ height: 200 }}>
            <Notification />
          </div>
        ) : isAdjusted ? (
          renderContentIsAdjusted()
        ) : !scoreCompany ? (
          renderContentFG18()
        ) : (
          renderContentFGOther()
        )}
      </div>
    </Panel>
  )
}

export default Commentary
